import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { V3CanvasHomeFeaturedListParams } from './type';

interface FeaturedPage<T> {
  list: T[];
  locale: string;
  has_more: boolean;
  next_cursor: string;
}

/** 按精选投放主键去重，保留已展示卡片的位置和曝光序号。 */
function appendUnique<T extends { featured_id: string | number; }>(current: T[], incoming: T[]): T[] {
  const ids = new Set(current.map((item) => String(item.featured_id)));
  return incoming.reduce((result, item) => {
    const id = String(item.featured_id);
    if (!ids.has(id)) {
      ids.add(id);
      result.push(item);
    }
    return result;
  }, [...current]);
}

/** 首页与详情侧栏共用游标分页，列表请求不依赖当前选中案例。 */
export default function useFeaturedListPagination<T extends { featured_id: string | number; }>(
  params: V3CanvasHomeFeaturedListParams,
  requestPage: (query: V3CanvasHomeFeaturedListParams) => Promise<FeaturedPage<T>>,
  resetKey?: string,
) {
  const {
    placement, region, locale, category_id: categoryId, limit,
  } = params;
  const [page, setPage] = useState<{ list: T[]; locale: string; }>({ list: [], locale: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>();
  const [hasMore, setHasMore] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const busy = useRef(false);
  const loadMoreFailed = useRef(false);
  const cursor = useRef('');
  const canLoadMore = useRef(false);

  /** 重试首屏时重新取第一页，不沿用上一页游标。 */
  const refresh = useCallback(() => setReload((value) => value + 1), []);

  useEffect(() => {
    generation.current += 1;
    const currentGeneration = generation.current;
    busy.current = true;
    loadMoreFailed.current = false;
    cursor.current = '';
    canLoadMore.current = false;
    setPage({ list: [], locale: '' });
    setHasMore(false);
    setLoading(true);
    setLoadingMore(false);
    setError(undefined);
    setMoreError(undefined);

    /** 首屏不传 cursor；响应过期时不覆盖新投放条件的列表。 */
    async function loadFirstPage() {
      try {
        const result = await requestPage({
          placement, region, locale, category_id: categoryId, limit,
        });
        if (currentGeneration !== generation.current) return;
        cursor.current = result.next_cursor || '';
        canLoadMore.current = result.has_more === true && Boolean(cursor.current);
        setPage({ list: appendUnique([], result.list ?? []), locale: result.locale });
        setHasMore(canLoadMore.current);
      } catch (failure) {
        if (currentGeneration === generation.current) setError(failure);
      } finally {
        if (currentGeneration === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    }

    loadFirstPage();
    return () => { generation.current += 1; };
  }, [placement, region, locale, categoryId, limit, requestPage, resetKey, reload]);

  /** 触底时仅请求一个游标；失败后保留旧列表并停止自动请求。 */
  const loadMore = useCallback(async () => {
    if (busy.current || loadMoreFailed.current || !canLoadMore.current || !cursor.current) return;
    const currentGeneration = generation.current;
    const nextCursor = cursor.current;
    busy.current = true;
    setLoadingMore(true);
    try {
      const result = await requestPage({
        placement, region, locale, category_id: categoryId, limit, cursor: nextCursor,
      });
      if (currentGeneration !== generation.current) return;
      cursor.current = result.next_cursor || '';
      canLoadMore.current = result.has_more === true && Boolean(cursor.current) && cursor.current !== nextCursor;
      setPage((current) => ({
        list: appendUnique(current.list, result.list ?? []), locale: result.locale || current.locale,
      }));
      setHasMore(canLoadMore.current);
    } catch (failure) {
      if (currentGeneration === generation.current) {
        loadMoreFailed.current = true;
        setMoreError(failure ?? true);
      }
    } finally {
      if (currentGeneration === generation.current) {
        busy.current = false;
        setLoadingMore(false);
      }
    }
  }, [placement, region, locale, categoryId, limit, requestPage]);

  return {
    list: page.list,
    responseLocale: page.locale,
    loading,
    error,
    loadingMore,
    moreError,
    hasMore,
    nextCursor: cursor.current,
    refresh,
    loadMore,
  };
}
